import * as Sentry from "@sentry/react";

import { isAppStale, markAppStale } from "./staleAppState";

// How often to poll /version.json while the tab is open.
const VERSION_POLL_INTERVAL = 10 * 60 * 1000;
// Min time since the last poll before re-checking on tab focus (avoids rapid tab-switch spam).
const MIN_VISIBILITY_CHECK_INTERVAL = 60 * 1000;
// sessionStorage key recording when this tab last attempted a stale-asset reload.
const RELOAD_GUARD_KEY = "rta-stale-reload-at";
// Min time between reload attempts per tab; prevents loops when the CDN still serves stale index.html.
const MIN_RELOAD_INTERVAL = 60 * 1000;
let staleReloadPending = false;

// Messages browsers produce when a dynamic import fails to fetch (typically a
// content-hashed chunk that was removed by a newer deploy).
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export const isChunkLoadError = (error: unknown): boolean => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  return CHUNK_LOAD_ERROR_PATTERN.test(message);
};

export const getBuildId = () =>
  document.querySelector('meta[name="build-id"]')?.getAttribute("content") ??
  null;

/**
 * Reload the page to pick up fresh assets, at most once per minute per tab.
 * The CDN edge can keep serving a stale index.html for a few minutes after a
 * deploy, so an unguarded reload-on-failure could loop. Returns false when
 * the guard blocks the reload; callers should fall back to UI.
 */
export const attemptStaleReload = (): boolean => {
  if (staleReloadPending) {
    return true;
  }

  try {
    const lastAttempt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY));
    if (lastAttempt && Date.now() - lastAttempt < MIN_RELOAD_INTERVAL) {
      return false;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable: reload anyway rather than strand the user
  }
  staleReloadPending = true;
  window.location.reload();
  return true;
};

export const reportVersionSkew = (source: string, error?: unknown) => {
  Sentry.withScope((scope) => {
    scope.setTag("deploy_version_skew", source);
    scope.setLevel("warning");
    if (error) scope.setExtra("originalError", String(error));
    Sentry.captureMessage("Deploy version skew: stale chunk request");
  });
};

let chunkLoadSkewReported = false;

const reportChunkLoadSkewOnce = (source: string, error?: unknown) => {
  if (chunkLoadSkewReported) return;
  chunkLoadSkewReported = true;
  reportVersionSkew(source, error);
};

const handleChunkLoadFailure = (source: string, error?: unknown) => {
  reportChunkLoadSkewOnce(source, error);
  if (attemptStaleReload()) return;

  // Allow chunk-load-error to upgrade an existing version-poll stale state.
  markAppStale("chunk-load-error");
};

const getPreloadError = (event: Event): unknown => {
  if ("payload" in event) return event.payload;
  if (event instanceof CustomEvent) return event.detail;
  return undefined;
};

const checkForNewVersion = async (currentBuildId: string) => {
  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return;
    const data = await res.json();
    if (data?.buildId && data.buildId !== currentBuildId) {
      markAppStale("version-poll");
    }
  } catch {
    // missing/invalid version.json (dev server, mid-deploy race): ignore
  }
};

let initialized = false;

export const initDeployVersionGuard = () => {
  if (initialized) return;
  initialized = true;

  // Vite dispatches this when a dynamic import (or one of its preloaded deps)
  // fails. No preventDefault: the error still propagates so local handling
  // (React.lazy, PDF export try/catch) behaves as it does today.
  window.addEventListener("vite:preloadError", (event) => {
    handleChunkLoadFailure("vite:preloadError", getPreloadError(event));
  });

  // Catch-all for dynamic imports without local error handling.
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      handleChunkLoadFailure("unhandledrejection", event.reason);
    }
  });

  // The build-id meta tag only exists in built output, so polling is a no-op
  // in dev.
  const buildId = getBuildId();
  if (!buildId) return;

  let lastCheck = Date.now();
  const check = () => {
    if (isAppStale()) return;
    lastCheck = Date.now();
    checkForNewVersion(buildId);
  };

  setInterval(check, VERSION_POLL_INTERVAL);
  document.addEventListener("visibilitychange", () => {
    if (
      document.visibilityState === "visible" &&
      Date.now() - lastCheck > MIN_VISIBILITY_CHECK_INTERVAL
    ) {
      check();
    }
  });
};
